import type { ChartUnit } from '../../../core/types/chart-unit.js';
import type { TimeseriesChartXAxisProps } from '../../../timeseries/slots/XAxis.js';
import type { TimeseriesChartYAxisProps } from '../../../timeseries/slots/YAxis.js';
import type { XYChartTimeAxisProps } from '../../../xy-chart/types/xy-chart-props.js';

type TimeAxisFallback = 'data-min' | 'data-max';

function normalizeTimeAxisBound(
  value: number | string | Date | undefined,
  fallback: 'data-min',
): 'data-min' | number | Date;
function normalizeTimeAxisBound(
  value: number | string | Date | undefined,
  fallback: 'data-max',
): 'data-max' | number | Date;
function normalizeTimeAxisBound(
  value: number | string | Date | undefined,
  fallback: TimeAxisFallback,
): TimeAxisFallback | number | Date {
  if (value === undefined || (value as unknown) === 'auto') {
    return fallback;
  }
  if (typeof value === 'string') {
    return new Date(value);
  }
  return value;
}

export type ConfiguredAxis = {
  position: 'left' | 'right';
  config: TimeseriesChartYAxisProps;
};

export function mapYAxisSlots(
  leftYAxis: TimeseriesChartYAxisProps | undefined,
  rightYAxis: TimeseriesChartYAxisProps | undefined,
  allUnits: ChartUnit[],
): { configuredAxes: ConfiguredAxis[]; renderedUnits: ChartUnit[] } {
  const configuredAxes = [
    leftYAxis ? { position: 'left' as const, config: leftYAxis } : null,
    rightYAxis ? { position: 'right' as const, config: rightYAxis } : null,
  ].filter((entry): entry is ConfiguredAxis => entry !== null);

  const renderedUnits =
    configuredAxes.length > 0
      ? allUnits.slice(0, configuredAxes.length)
      : allUnits;

  return { configuredAxes, renderedUnits };
}

export function mapYAxisSlotToProps(
  index: number,
  axisEntry: ConfiguredAxis | undefined,
) {
  const defaultPosition = index % 2 === 0 ? 'left' : 'right';
  const position = axisEntry ? axisEntry.position : defaultPosition;
  const config = axisEntry?.config;
  return {
    type: config?.scale === 'log' ? ('log' as const) : ('numerical' as const),
    position,
    min: config?.min ?? ('auto' as const),
    max: config?.max ?? ('auto' as const),
    label: config?.label,
    formatter: config?.formatter,
  };
}

export function mapXAxisSlot(
  xAxis: TimeseriesChartXAxisProps | undefined,
): Pick<XYChartTimeAxisProps, 'min' | 'max' | 'label'> {
  return {
    min: normalizeTimeAxisBound(xAxis?.min, 'data-min'),
    max: normalizeTimeAxisBound(xAxis?.max, 'data-max'),
    label: xAxis?.label,
  };
}
